/*
 * Authors: mariushirjanu
 * Created: 31/05/2022
 */
import { util } from '../../../shared/util';
import { Component, Input, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { TranslationService } from '../../../shared/translation/translation.service';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { FormattingService } from '../../../shared/services/formatting.service';
import { ILocationExtended } from '../../../../type_definitions/datatypes';
import { IOrderFeesHeader, OrderFeesDataService } from './order-fees-data.service';
import { PAYMENTTYPE_CC, PAYMENTTYPE_PO } from '../../../shared/services/billing/billing-data.service';
import { CountryDataService } from '../../../shared/location/country-data.service';
import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import { FormlyAlertComponent } from '../../../shared/formly/components/formly-alert.component';
import { GA1Service } from '../../../shared/ga1/ga1.service';
import { DialogSize, GaMessageService, IDialog } from '../../../shared/message/ga-message.service';
import { IncludedOrdersDialogComponent } from './included-orders-dialog.component';
import { TitleService } from '../../../shared/services/title.service';
import { ConfirmProformaDialogComponent } from './confirm-proforma-dialog.component';
import { RejectProformaDialogComponent } from './reject-proforma-dialog.component';

import { PAYMENT_STATUS_OPEN, PAYMENT_STATUS_REFUSED, PaymentDataService } from '../../payment/payment-data.service';
import { PaymentFormattingService } from '../../payment/payment-formatting.service';
import { ConfirmPaymentDialogComponent } from './confirm-payment-dialog.component';
import { Alert } from '../../../shared/app-styles';
import { GaWizard, IWizardButton } from '../../../shared/wizard/ga-wizard';
import { GaWizardService } from '../../../shared/wizard/ga-wizard.service';
import { BPProfilesLocationsDataService } from '../../../shared/bp-profiles/bp-profiles-locations-data.service';
export const ORDER_FEES_WIZARD:string = 'orderfees';
const SAVE_ACTION:string = 'saveOrderFees';

export enum InvoiceStatus {
  UPCOMING = 'UPCOMING',
  PROFORMA = 'PROFORMA',
  SEND = 'SEND',
  BOOKED = 'BOOKED',
  CANCEL = 'CANCEL'
}

export enum ProformaStatus {
  SENT = 'SENT',
  REJECTED = 'REJECTED',
  ACCEPTED = 'ACCEPTED'
}

export enum PaymentStatus {
  PAID_WAITING = 'PAID_WAITING'
}

@Component({
  selector: 'order-fees',
  template: `
    <div class="order-fees">
      <div class="row" *ngIf="!loading">
        <div class="col-md-6">
          <h3>
            <text key="INVOICE_DETAILS"></text>
          </h3>
          <formly [form]="form" [fields]="fields[0]" [model]="model"></formly>
        </div>
        <div class="col-md-6">
          <h3>
            <text key="INVOICE_ADDRESS"></text>
          </h3>
          <formly [form]="form" [fields]="fields[1]" [model]="model"></formly>
        </div>
      </div>
      <h3 *ngIf="loading">
        <activity-indicator useDefaultText="true"></activity-indicator>
      </h3>
    </div>
  `,
  styleUrls: ['./order-fees.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OrderFeesComponent implements OnInit, OnDestroy {
  @Input() public invoiceId:number;
  private wizard:GaWizard;
  public title:string;
  public loading:boolean;
  public wizardId:string = ORDER_FEES_WIZARD;
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public model:IOrderFeesHeader;
  public fields:IExtraFieldConfigurationObject[][];
  private locationField:IExtraFieldConfigurationObject;
  private changePaymentTypeLabelField:IExtraFieldConfigurationObject;
  private stateField:IExtraFieldConfigurationObject;
  private vatField:IExtraFieldConfigurationObject;
  private rejectionMessageField:IExtraFieldConfigurationObject;
  private confirmPaymentMessageField:IExtraFieldConfigurationObject;
  private downloadInvoiceProformaLinkField:IExtraFieldConfigurationObject;
  private payNowButton:IExtraFieldConfigurationObject;

  public isStatusUpcoming:Function = () => this.model?.invoiceStatus === InvoiceStatus.UPCOMING;
  public isStatusProforma:Function = () => this.model?.invoiceStatus === InvoiceStatus.PROFORMA && this.model?.proformaStatus !== ProformaStatus.REJECTED;
  public isStatusSendOrBooked:Function = () => this.model?.invoiceStatus === InvoiceStatus.SEND || this.model?.invoiceStatus === InvoiceStatus.BOOKED;
  public isStatusCancel:Function = () => this.model?.invoiceStatus === InvoiceStatus.CANCEL;
  public isProformaRejected:Function = () => this.model?.invoiceStatus === InvoiceStatus.PROFORMA && this.model?.proformaStatus === ProformaStatus.REJECTED;

  constructor(
    private bpProfilesLocationsDataService:BPProfilesLocationsDataService,
    private countryDataService:CountryDataService,
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private ga1Service:GA1Service,
    private messageService:GaMessageService,
    private orderFeesDataService:OrderFeesDataService,
    private paymentDataService:PaymentDataService,
    private paymentFormattingService:PaymentFormattingService,
    private titleService:TitleService,
    private translationService:TranslationService,
    private validateService:GaValidateService,
    private wizardService:GaWizardService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.wizard = this.wizardService.getInstance(ORDER_FEES_WIZARD);
    this.start();
  }

  private start():void {
    this.fields = this.createFields();

    this.loading = true;
    this.orderFeesDataService.loadOrderFeesHeader(this.invoiceId)
      .then((invoiceHeader:IOrderFeesHeader) => {
        this.afterLoad(invoiceHeader);
        this.initWizardSetup();
        this.fillFields();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private fillFields():void {
    this.formlyService.fillField(this.locationField, this.bpProfilesLocationsDataService.getLocations(this.model.bpId, this.model.countryReceiver?.id));

    if(this.model?.countryReceiver?.hasState){
      this.formlyService.fillField(this.stateField, this.countryDataService.getStates(this.model?.countryReceiver?.id));
    }
  }

  private onLocationChange = (location:ILocationExtended):void => {
    this.form.patchValue({
      company: location?.company,
      addressReceiver: location?.address,
      zipReceiver: location?.zipCode,
      cityReceiver: location?.city
    });
  };

  private initWizardSetup():void {
    const saveActionButton:IWizardButton = this.wizardService.createActionButton(
      SAVE_ACTION,
      this.translationService.instant('GA.BUTTONS.SAVE_LABEL', {label: 'INVOICE.ORDERFEES.INVOICE_ADDRESS.TITLE'}),
      'btn btn-success',
      this.saveInvoiceInfo
    );

    const actions:IWizardButton[] = [];
    if(!(this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel())){
      actions.push(saveActionButton);
    }
    this.wizard.actions(actions);
    this.setTitle();
  }

  private setTitle():void {
    let titleKey:string;
    if(this.isStatusUpcoming()){
      titleKey = 'UPCOMING';
    } else if(this.isStatusProforma()){
      titleKey = 'PROFORMA';
    } else if(this.isStatusSendOrBooked()){
      titleKey = 'APPROVED';
    } else if(this.isProformaRejected()){
      titleKey = 'REJECTED';
    } else if(this.isStatusCancel()){
      titleKey = 'CANCEL';
    } else {
      // use default 'Order fee invoice' (prevent showing an undefined title)
      titleKey = 'APPROVED';
    }
    this.wizard.title = this.translationService.instant(`INVOICE.ORDERFEES.HEADINGS.${titleKey}.TEXT`);
    this.titleService.setTitle(this.wizard.title);
  }

  private createFields():IExtraFieldConfigurationObject[][] {
    const labelClass:string = 'col-md-3';
    const valueClass:string = 'col-md-9';

    const invoiceDetailsFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aValue('subject')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aValue('poNumber')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => !this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel())
        .build(),

      this.formlyService.aValue('period')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aValue('invoiceNumber')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => !this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel())
        .build(),

      this.formlyService.aValue('paymentTotal', 'GA.GENERAL.TOTAL')
        .withFormat((amount:number) => {
          return this.formattingService.formatAmount(amount, this.model?.currency);
        })
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aValue('dueDate')
        .withFormat(this.formattingService.formatDate)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aGroup()
        .withEditable('paymentType')
        .withFields([
          this.formlyService.aValue('paymentType')
            .withoutWrapper()
            .withWrapperClass('col-sm-4')
            .withFormat((paymentType:string) => {
              return paymentType ? this.translationService.instant(paymentType === PAYMENTTYPE_PO ? 'GA.GENERAL.BANK_TRANSFER' : 'GA.GENERAL.CREDIT_CARD'): '';
            })
            .build(),

          this.changePaymentTypeLabelField = this.formlyService.aLink('GA.BUTTONS.CHANGE_TO_LABEL')
            .withWrapperClass('col-sm-8')
            .withoutWrapper()
            .onClick(this.changePaymentType)
            .withExtraOptions({
              templateOptions: {
                linkClass: 'btn'
              }
            })
            .withHideExpression(() => !this.model?.paymentType || this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel())
            .build(),

          this.formlyService.aValue()
            .withoutWrapper()
            .withWrapperClass('col-sm-4')
            .withHideExpression(() => !(this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel()))
            .build()
        ])
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aValue('paymentStatus', 'ORDERENTRY.PAYMENT.STATUS')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat((paymentStatus:string) => this.paymentFormattingService.formatPaymentStatus(paymentStatus, undefined, this.model.paymentTotal, this.model.paymentCurrency, this.model.paymentDate))
        .withHideExpression(() => !this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel() || this.model?.paymentType !== PAYMENTTYPE_CC)
        .build(),

      this.formlyService.aTemplate(`
        <div class="alert alert-warning">
          <i class="fa fa-exclamation-triangle"></i>
          <span>${this.translationService.instant('INVOICE.ORDERFEES.CHANGE_PAYMENT_WARNING.TEXT')}</span>
        </div>
      `)
        .withHideExpression(() => !this.model?.paymentType || this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel())
        .build(),

      this.formlyService.aGroup('confirmRejectProforma')
        .withFields([
          this.formlyService.aLink('GA.BUTTONS.CONFIRM')
            .withoutWrapper()
            .withWrapperClass('col-lg-4 col-md-5 col-sm-4 col-4')
            .withExtraOptions({
              templateOptions: {
                linkClass: 'btn btn-success'
              }
            })
            .onClick(this.confirmProforma)
            .build(),

          this.formlyService.aLink('GA.BUTTONS.REJECT')
            .withoutWrapper()
            .withWrapperClass('col-lg-4 col-md-5 col-sm-4 col-4')
            .withExtraOptions({
              templateOptions: {
                linkClass: 'btn btn-danger'
              }
            })
            .onClick(this.rejectProforma)
            .build()
        ])
        .withHideExpression(() => !this.isStatusProforma())
        .withLabelClass('col-sm-6')
        .withValueClass('col-sm-6')
        .build(),

      this.rejectionMessageField = this.formlyService.aCustom()
        .withComponent(FormlyAlertComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              alertClass: `${Alert.DANGER} no-margin`
            }
          }
        })
        .withHideExpression(() => !this.isProformaRejected())
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .build(),

      this.formlyService.aCustom()
        .withComponent(FormlyAlertComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              message: this.translationService.instant('INVOICE.ORDERFEES.INVOICE_CANCEL_HEADER.TEXT'),
              alertClass: `${Alert.DANGER} no-margin`
            }
          }
        })
        .withHideExpression(() => !this.isStatusCancel())
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .build(),

      this.formlyService.aValue('proformaComment')
        .withoutWrapper()
        .withWrapperClass('triple-space-bottom')
        .withHideExpression(() => !this.isProformaRejected())
        .build(),

      this.formlyService.aValue('invoiceComment')
        .withoutWrapper()
        .withWrapperClass('triple-space-bottom')
        .withHideExpression(() => !this.isStatusCancel())
        .build(),

      this.formlyService.aGroup()
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .withFields([
          this.formlyService.aLink('INVOICE.ORDERFEES.CONFIRM_INCLUDED_ORDERS.LABEL')
            .withoutWrapper()
            .withWrapperClass('col-lg-5 col-md-5 col-sm-4 col-sm-max-space-bottom')
            .withExtraOptions({
              templateOptions: {
                linkClass: 'btn btn-primary no-margin'
              }
            })
            .onClick(this.confirmIncludedOrders)
            .withHideExpression(() => !this.isStatusUpcoming())
            .build(),

          this.payNowButton = this.formlyService.aLink('INVOICE.ORDERFEES.PAY_NOW.LABEL')
            .withoutWrapper()
            .withWrapperClass('col-md-3 col-sm-2 col-5')
            .withExtraOptions({
              templateOptions: {
                linkClass: 'btn btn-primary'
              }
            })
            .onClick(this.payInvoice)
            .withHideExpression(() => !this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel() || this.model?.paymentType !== PAYMENTTYPE_CC || !(this.model?.paymentStatus === PAYMENT_STATUS_OPEN || this.model.paymentStatus === PAYMENT_STATUS_REFUSED))
            .build(),

          this.formlyService.aLink('INVOICE.ORDERFEES.CONFIRM_PAYMENT.TEXT')
            .withoutWrapper()
            .withWrapperClass('col-md-4 col-sm-3 col-5')
            .withExtraOptions({
              templateOptions: {
                linkClass: 'btn btn-success'
              }
            })
            .onClick(this.confirmPayment)
            .withHideExpression(() => !this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel() || this.model?.paymentType !== PAYMENTTYPE_PO || !(this.model?.paymentStatus === PAYMENT_STATUS_OPEN || this.model.paymentStatus === PAYMENT_STATUS_REFUSED))
            .build(),

          this.downloadInvoiceProformaLinkField = this.formlyService.aLink('INVOICE.ORDERFEES.DOWNLOAD_PROFORMA.LABEL')
            .withWrapperClass('col col-auto col-sm-max-space-bottom')
            .withoutWrapper()
            .withExtraOptions({
              templateOptions: {
                linkClass: 'btn',
                href: `rest/orderfees/${this.invoiceId}/download`,
                target: '_blank'
              }
            })
            .withHideExpression(() => this.isStatusUpcoming() || this.isStatusCancel())
            .build(),

          this.formlyService.aLink('INVOICE.ORDERFEES.CHANGE_INCLUDED_ORDERS.LABEL')
            .withoutWrapper()
            .withWrapperClass('col col-auto col-sm-max-space-bottom')
            .withExtraOptions({
              templateOptions: {
                linkClass: 'btn no-margin'
              }
            })
            .onClick(this.changeIncludedOrders)
            .withHideExpression(() => !(this.isStatusUpcoming() || this.isStatusProforma()))
            .build()
        ])
        .build(),

      this.confirmPaymentMessageField = this.formlyService.aCustom()
        .withComponent(FormlyAlertComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              alertClass: `${Alert.SUCCESS} no-margin`
            }
          }
        })
        .withHideExpression(() => this.model?.paymentStatus !== PaymentStatus.PAID_WAITING)
        .withWrapperClass('col-12 double-space-top')
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .build(),

      this.formlyService.aValue('paymentComment')
        .withoutWrapper()
        .withHideExpression(() => this.model?.paymentStatus !== PaymentStatus.PAID_WAITING)
        .build()
    ];

    const invoiceAddressFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aValue('countryReceiver', 'GA.GENERAL.COUNTRY')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatCountry)
        .withHideExpression(() => this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel())
        .build(),

      this.formlyService.anInput('registeredNameReceiver', 'GA.GENERAL.NAME')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel())
        .build(),

      this.locationField = this.formlyService.aSelect2('locationReceiver', 'GA.GENERAL.LOCATION')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatLocationHtml)
        .withExtraOptions({
          templateOptions: {
            parseHtml: true
          }
        })
        .withHideExpression(() => this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel())
        .onChange(this.onLocationChange)
        .build()
    ];


    const addressField:IExtraFieldConfigurationObject = this.formlyService.anInput('addressReceiver', 'GA.GENERAL.ADDRESS')
      .isRequired()
      .withoutWrapper()
      .withWrapperClass('col-md-12 col-space-bottom')
      .withPlaceholder()
      .build();
    const zipField:IExtraFieldConfigurationObject = this.formlyService.anInput('zipReceiver', 'GA.GENERAL.ZIP')
      .withoutWrapper()
      .withWrapperClass('col-4 col-xsm-5 col-sm-4 col-hug-right')
      .build();
    const cityField:IExtraFieldConfigurationObject = this.formlyService.anInput('cityReceiver', 'GA.GENERAL.CITY')
      .isRequired()
      .withoutWrapper()
      .withWrapperClass('col-8 col-xsm-7 col-sm-8 col-hug-left')
      .withPlaceholder()
      .build();
    const zipCityGroup:IExtraFieldConfigurationObject = this.formlyService.aGroup('zipCity')
      .withFields([
        zipField,
        cityField
      ])
      .withoutWrapper()
      .withWrapperClass('col-md-12 col-space-bottom')
      .withPlaceholder()
      .build();

    this.stateField = this.formlyService.aSelect('stateReceiver', 'GA.GENERAL.STATE')
      .withFormat(this.formattingService.formatState)
      .withoutWrapper()
      .withWrapperClass('col-md-12')
      .withHideExpression(() => !this.model?.countryReceiver?.hasState || this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel())
      .build();

    invoiceAddressFields.push(
      this.formlyService.aGroup(undefined, 'GA.GENERAL.ADDRESS')
        .withFields([
          addressField,
          zipCityGroup,
          this.stateField
        ])
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel())
        .build(),

      this.vatField = this.formlyService.anInput('taxIdReceiver')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel())
        .build(),

      this.formlyService.anInput('contactNameReceiver')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel())
        .build(),

      this.formlyService.anInput('contactEmailReceiver', 'GA.GENERAL.EMAIL')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel())
        .build()

    );

    invoiceAddressFields.push(
      this.formlyService.aValue('registeredNameReceiver')
        .withoutWrapper()
        .withHideExpression(() => !(this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel()))
        .withFormat((name:string) => {
          return `<b>${name}</b>`;
        })
        .build(),

      this.formlyService.aValue('locationReceiver')
        .withoutWrapper()
        .withFormat(() => {
          const editedLocation:ILocationExtended = util.deepClone(this.model?.locationReceiver || {});
          editedLocation.address = this.model?.addressReceiver;
          editedLocation.city = this.model?.cityReceiver;
          editedLocation.zipCode = this.model?.zipReceiver;
          editedLocation.country = this.model?.countryReceiver;

          return this.formattingService.formatLocationHtml(editedLocation, {
            name: this.model?.countryReceiver?.vatName,
            number: this.model?.taxIdReceiver
          });
        })
        .withHideExpression(() => !(this.isStatusSendOrBooked() || this.isProformaRejected() || this.isStatusCancel()))
        .build(),

      this.formlyService.aValue('contactNameReceiver')
        .withoutWrapper()
        .withHideExpression(() => !this.isStatusSendOrBooked())
        .withFormat(() => {
          const mailTo:string = this.model?.contactEmailReceiver ? `<a href="mailto:${this.model?.contactEmailReceiver}">${this.model?.contactEmailReceiver}</a>` : undefined;
          return this.formattingService.join(this.model?.contactNameReceiver, mailTo, '<br />');
        })
        .build()
    );

    return [
      // left column
      invoiceDetailsFields,
      // right column
      invoiceAddressFields
    ];
  }

  private getRejectionAlert = (): string => {
    const ga1Url:string = this.ga1Service.getGA1Url(GA1Service.URLTYPE_SUPPORT_TICKET, {
      row0_Demndid: this.model?.supportTicket,
      ejbEvent: 'edit'
    }, {
      redirect: true,
      hideMenu: false,
      hideTop: false
    });

    return this.translationService.instant('INVOICE.ORDERFEES.PROFORMA_REJECTION_REASON.TEXT', {
      date: this.formattingService.formatDate(this.model?.proformaStatusDate),
      ticketId: `<a href="${ga1Url}" target="_blank">${this.model?.supportTicket}</a>`
    });
  };

  private getConfirmPaymentAlert = (): string => {
    return this.translationService.instant('INVOICE.ORDERFEES.PAYMENT_CONFIRMATION.TEXT', {
      date: this.formattingService.formatDate(this.model?.paymentDate)
    });
  };


  private updatePaymentTypeButtonLabel():void {
    if(this.model?.paymentType){
      this.changePaymentTypeLabelField.templateOptions.label = this.translationService.instant('GA.BUTTONS.CHANGE_TO_LABEL', { label: this.translationService.instant(this.model.paymentType === PAYMENTTYPE_PO ? 'GA.GENERAL.CREDIT_CARD' : 'GA.GENERAL.BANK_TRANSFER') });
    }
  }

  private saveInvoiceInfo = ():void => {
    // show form errors when the invoice address form is invalid
    this.validateService.validateFields(this.fields[1], this.model, true)
      .then(() => {
        this.wizard.setActionBusy(SAVE_ACTION, true);
        this.orderFeesDataService.saveInvoiceAddress(this.invoiceId, this.model)
          .then((invoiceAddress:IOrderFeesHeader) => {
            this.model = util.deepClone(invoiceAddress);
          })
          .finally(() => {
            this.wizard.setActionBusy(SAVE_ACTION, false);
          });
      });
  };

  private changePaymentType = ():void => {
    const newPaymentType:string = this.model?.paymentType === PAYMENTTYPE_PO ? PAYMENTTYPE_CC : PAYMENTTYPE_PO;
    this.orderFeesDataService.changePaymentType(this.invoiceId, newPaymentType)
      .then((invoiceHeader:IOrderFeesHeader) => {
        this.model = util.deepClone(invoiceHeader);
        this.updatePaymentTypeButtonLabel();
      });
  };

  private changeIncludedOrders = ():void => {
    const changeIncludedOrdersDialog:IDialog = this.messageService.showDialog({
      component: IncludedOrdersDialogComponent,
      title: 'INVOICE.ORDERFEES.INCLUDED_ORDERS.TEXT',
      buttons: [ {
        label: 'GA.BUTTONS.CLOSE',
        className: 'btn btn-danger'
      } ],
      inputs: {
        companyCountryId: this.model.idCompanyCountryReceiver,
        invoiceId: this.invoiceId
      },
      outputs: {
        onActionExecuted: () => {
          // reload price
          this.reloadOrderFeeInvoiceHeader();
          changeIncludedOrdersDialog.hide();
        }
      },
      size: DialogSize.XL
    });
  };

  private payInvoice = ():void => {
    this.payNowButton.templateOptions.busy = true;
    this.paymentDataService.payWithStripe(undefined, this.invoiceId, window.top.location.href)
      .finally(() => {
        this.payNowButton.templateOptions.busy = false;
      });
  };

  private confirmIncludedOrders = ():void => {
    this.confirmProforma('INVOICE.ORDERFEES.CONFIRM_INCLUDED_ORDERS.LABEL');
  };

  private confirmProforma = (title:string):void => {
    const confirmProformaDialog:IDialog = this.messageService.showDialog({
      component: ConfirmProformaDialogComponent,
      wrapperComponent: false,
      inputs: {
        title: title || 'INVOICE.ORDERFEES.CONFIRM_PROFORMA.TEXT',
        invoiceId: this.invoiceId,
        dueDate: this.model.dueDate
      },
      outputs: {
        onSaved: (invoiceHeader:IOrderFeesHeader) => {
          this.afterLoad(invoiceHeader);
          this.wizard.setActionVisible(SAVE_ACTION, false);
          this.setTitle();
          confirmProformaDialog.hide();
        }
      },
      size: DialogSize.MD
    });
  };
  
  private confirmPayment = ():void => {
    const confirmPaymentDialog:IDialog = this.messageService.showDialog({
      component: ConfirmPaymentDialogComponent,
      wrapperComponent: false,
      inputs: {
        title: 'INVOICE.ORDERFEES.CONFIRM_PAYMENT.TEXT',
        invoiceId: this.invoiceId,
        paymentTotal: this.model.paymentTotal,
        currency: this.model.currency
      },
      outputs: {
        onSaved: (invoiceHeader:IOrderFeesHeader) => {
          this.afterLoad(invoiceHeader);
          this.wizard.setActionVisible(SAVE_ACTION, false);
          this.setTitle();
          confirmPaymentDialog.hide();
        }
      },
      size: DialogSize.MD
    });
  };

  private rejectProforma = ():void => {
    const rejectProformaDialog:IDialog = this.messageService.showDialog({
      component: RejectProformaDialogComponent,
      wrapperComponent: false,
      buttons: [ {
        label: 'GA.BUTTONS.CLOSE',
        className: 'btn btn-danger'
      } ],
      inputs: {
        title: 'INVOICE.ORDERFEES.REJECT_PROFORMA.TEXT',
        invoiceId: this.invoiceId
      },
      outputs: {
        onSaved: (invoiceHeader:IOrderFeesHeader) => {
          this.afterLoad(invoiceHeader);
          this.wizard.setActionVisible(SAVE_ACTION, false);
          this.setTitle();
          rejectProformaDialog.hide();
        }
      },
      size: DialogSize.MD
    });
  };

  private afterLoad(invoiceHeader:IOrderFeesHeader):IOrderFeesHeader {
    this.model = util.deepClone(invoiceHeader);
    this.vatField.templateOptions.required = this.model?.countryReceiver?.vatMandatory;
    this.vatField.templateOptions.label = this.model?.countryReceiver?.vatName || 'GA.GENERAL.VATTAX';
    if(this.isStatusSendOrBooked() && !this.isProformaRejected()) {
      this.downloadInvoiceProformaLinkField.templateOptions.label = 'INVOICE.ORDERFEES.DOWNLOAD_INVOICE.LABEL';
    }
    this.rejectionMessageField.templateOptions.inputs.message = this.getRejectionAlert();
    this.confirmPaymentMessageField.templateOptions.inputs.message = this.getConfirmPaymentAlert();
    this.updatePaymentTypeButtonLabel();
    return invoiceHeader;
  }

  private reloadOrderFeeInvoiceHeader():Promise<IOrderFeesHeader> {
    return this.orderFeesDataService.loadOrderFeesHeader(this.invoiceId)
      .then((invoiceHeader:IOrderFeesHeader) => {
        this.model = util.deepClone(invoiceHeader);
        return invoiceHeader;
      });
  }

  public ngOnDestroy():void {
    this.wizard.clear();
    this.countryDataService.clear();
  }
}